import axios from "axios";
import { ReqMethods, ResponseStatus } from "enums";
import env from "config";
import { setNotification, useNotifcation } from "context/notification";
import { useAuth, useLogout } from "./auth";

const BASE_URL = `${env.API_URL}/v1`;
function useServiceHandler() {
  const [, dispatch] = useNotifcation();
  const Logout = useLogout();

  return (fn) =>
    async (method, path, { query, payload, token, apiKey, toaster, message, error } = {}) => {
      try {
        const res = await fn(method, path, { query, payload, token, apiKey });
        toaster &&
          setNotification(dispatch, {
            open: true,
            message: message || res.data.message,
            severity: "success",
            title: "Success",
          });

        return { response: res.data };
      } catch (err) {
        // expire error : jwt expired
        if (err.response && err.response.status === ResponseStatus.UNAUTHORIZED) {
          // setNotification(dispatch, {
          //   open: true,
          //   message: error || "session expired",
          //   title: "Error",
          //   severity: "error",
          // });
          // setTimeout(Logout, 4000);
          return { error: err.response?.data || err };
        }

        const customError = err.response?.data?.error
          ? `${err.response?.data?.message} \n${err.response?.data?.error}`
          : err.response?.data?.message;

        toaster &&
          setNotification(dispatch, {
            open: true,
            message: error || customError || err.message,
            severity: "error",
            title: "Error",
          });
        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const authToken = useAuth();
  const serviceHandler = useServiceHandler();

  const CallService = (method, path, { query, payload, token = true, apiKey = null }) => {
    const pathname = query ? `${path}?${query}` : path;
    const config = {};

    if (token) config.headers = { "x-auth-token": `Bearer ${authToken || token}` };
    if (apiKey) config.headers = { apiKey };

    const details = {};

    if (payload) details.payload = payload;
    details.config = config;

    return axios[method](pathname, ...Object.values(details));
  };

  return serviceHandler(CallService);
}

function useAppServices() {
  const { GET, POST, PUT, DELETE } = ReqMethods;
  const CallService = useCallService();

  /**
    Option for service is the object that could has the following properties
    query, payload, token, apiKey
  */

  const APIs = {
    auth: {
      login: (options) => CallService(POST, `${BASE_URL}/auth/login`, options),
      signup: (options) => CallService(POST, `${BASE_URL}/auth/signup`, options),
      customuser: (options) => CallService(POST, `${BASE_URL}/auth/customuser`, options),
      CreateTeam: (options) => CallService(POST, `${BASE_URL}/auth/team`, options),
      EditTeam: (options) => CallService(PUT, `${BASE_URL}/auth/team`, options),
      GetTeam: (options) => CallService(GET, `${BASE_URL}/auth/team`, options),
      ForgotPassword: (options) => CallService(POST, `${BASE_URL}/auth/forget`, options),
      ResetPassword: (options) => CallService(POST, `${BASE_URL}/auth/reset`, options),
    },
    user: {
      get: (options) => CallService(GET, `${BASE_URL}/user`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/user/filter`, options),
      register: (options) => CallService(POST, `${BASE_URL}/user/register`, options),
      add_team: (options) => CallService(POST, `${BASE_URL}/user/add_team `, options),
      location_user: (options) => CallService(GET, `${BASE_URL}/user/location_user`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/user`, options),
      subscriber: (options) => CallService(GET, `${BASE_URL}/user/subscriber`, options),
      agency_user: (options) => CallService(GET, `${BASE_URL}/user/agency_user`, options),
    },
    roles: {
      create: (options) => CallService(POST, `${BASE_URL}/roles`, options),
      get: (options) => CallService(GET, `${BASE_URL}/roles`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/roles`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/roles`, options),
    },
    prompts: {
      createDocs: (options) => CallService(POST, `${BASE_URL}/prompts`, options),
      updateDocs: (options) => CallService(PUT, `${BASE_URL}/prompts`, options),
      Docs: (options) => CallService(POST, `${BASE_URL}/prompts`, options),
      get_filter: (options) => CallService(GET, `${BASE_URL}/prompts/filter`, options),
      get_admin_prompts: (options) =>
        CallService(GET, `${BASE_URL}/prompts/filter/admin-prompts`, options),
      get_filter_cat: (options) => CallService(GET, `${BASE_URL}/prompts/category/filter`, options),
      managepromptsdata: (options) =>
        CallService(GET, `${BASE_URL}/prompts/managepromptsdata`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/prompts/delete`, options),
      switchIdsManager: (options) =>
        CallService(POST, `${BASE_URL}/prompts/switchIdsManager`, options),
      promptGenerator: (options) =>
        CallService(POST, `${BASE_URL}/prompts/agency/generate`, options),
    },
    prompts_category: {
      createDocs: (options) => CallService(POST, `${BASE_URL}/prompts/category`, options),
      updateDocs: (options) => CallService(PUT, `${BASE_URL}/prompts/category`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/prompts/category/delete`, options),
    },
    prompts_sub_category: {
      create: (options) => CallService(POST, `${BASE_URL}/prompts/subcategory`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/prompts/subcategory`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/prompts/subcategory/filter`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/prompts/subcategory/delete`, options),
    },
    manageprompt: {
      generate: (options) => CallService(POST, `${BASE_URL}/prompts/generate`, options),
      allcategory: (options) => CallService(POST, `${BASE_URL}/manageprompt/allcategory`, options),
      selectedsubcategory: (options) =>
        CallService(POST, `${BASE_URL}/manageprompt/selectedsubcategory`, options),
      create: (options) => CallService(POST, `${BASE_URL}/manageprompt`, options),
      updateprompt: (options) =>
        CallService(POST, `${BASE_URL}/manageprompt/updateprompt`, options),
      getprompts: (options) => CallService(GET, `${BASE_URL}/manageprompt/getprompts`, options),
    },
    brand: {
      get: (options) => CallService(GET, `${BASE_URL}/brand`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/brand`, options),
      UpdateLocation: (options) => CallService(PUT, `${BASE_URL}/brand/update-location`, options),
      create: (options) => CallService(POST, `${BASE_URL}/brand`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/brand/filter`, options),
    },
    app: {
      get: (options) => CallService(GET, `${BASE_URL}/app`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/app`, options),
      create: (options) => CallService(POST, `${BASE_URL}/app`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/app`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/app/filter`, options),
    },
    newsfeed: {
      get: (options) => CallService(GET, `${BASE_URL}/feeds`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/feeds/update`, options),
      create: (options) => CallService(POST, `${BASE_URL}/feeds/create-feeds`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/feeds/delete`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/feeds/filter`, options),
    },
    offer: {
      get: (options) => CallService(GET, `${BASE_URL}/offer`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/offer`, options),
      create: (options) => CallService(POST, `${BASE_URL}/offer`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/offer`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/offer/filter`, options),
    },
    accounts: {
      get: (options) => CallService(GET, `${BASE_URL}/accounts`, options),
      getGhlTags: (options) => CallService(GET, `${BASE_URL}/accounts/getGhlTags`, options),
      GetAccountsForAgency: (options) => CallService(GET, `${BASE_URL}/accounts/GetAccountsForAgency`, options),
      SearchListing: (options) => CallService(GET, `${BASE_URL}/accounts/SearchListing`, options),
      DeleteConsumer: (options) => CallService(PUT, `${BASE_URL}/accounts/DeleteConsumer`, options),
      DeleteBusiness: (options) => CallService(PUT, `${BASE_URL}/accounts/DeleteBusiness`, options),
      GetIntrestedPersons: (options) =>
        CallService(GET, `${BASE_URL}/accounts/GetIntrestedPersons`, options),
      applyForEvent: (options) => CallService(GET, `${BASE_URL}/accounts/applyForEvent`, options),
      csvupload: (options) => CallService(POST, `${BASE_URL}/accounts/csvupload`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/accounts`, options),
      UpdateTags: (options) => CallService(PUT, `${BASE_URL}/accounts/UpdateTags`, options),
      SaveGoogleImages: (options) => CallService(PUT, `${BASE_URL}/accounts/SaveGoogleImages`, options),
      create: (options) => CallService(POST, `${BASE_URL}/accounts`, options),
      event_trigger: (options) => CallService(POST, `${BASE_URL}/accounts/event_trigger`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/accounts`, options),
      delete_with_contact: (options) =>
        CallService(DELETE, `${BASE_URL}/accounts/delete_with_contact`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/accounts/filter`, options),
      search: (options) => CallService(GET, `${BASE_URL}/accounts/search`, options),
      getbyid: (options) => CallService(GET, `${BASE_URL}/accounts/getbyid`, options),
      ReOrderBusiness: (options) => CallService(PUT, `${BASE_URL}/accounts/ReOrderBusiness`, options),
      uploadMediaToGHL: (options) =>
        CallService(POST, `${BASE_URL}/accounts/media-upload`, options),
      statistics: (options) => CallService(GET, `${BASE_URL}/accounts/statistics`, options),
      mapPinStats: (options) => CallService(GET, `${BASE_URL}/accounts/mapPinStats`, options),
      mapPinEstimate: (options) =>
        CallService(GET, `${BASE_URL}/accounts/mapPinEstimate`, options),
      saveMapPinLimit: (options) => CallService(PUT, `${BASE_URL}/accounts/mapPinLimit`, options),
    },
    business_types: {
      get: (options) => CallService(GET, `${BASE_URL}/business_types`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/business_types`, options),
      create: (options) => CallService(POST, `${BASE_URL}/business_types`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/business_types`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/business_types/filter`, options),
      get_all_types: (options) =>
        CallService(GET, `${BASE_URL}/business_types/get_all_types`, options),
    },
    deals: {
      create: (options) => CallService(POST, `${BASE_URL}/deals`, options),
      get: (options) => CallService(GET, `${BASE_URL}/deals`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/deals`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/deals`, options),
      getbyaccount: (options) => CallService(GET, `${BASE_URL}/deals/GetDealByAccount`, options),
    },
    leadsource: {
      lookupPlan: (options) =>
        CallService(GET, `${BASE_URL}/leadsource/plan-mapping`, options),
      bindFromPurchase: (options) =>
        CallService(POST, `${BASE_URL}/leadsource/bind-from-purchase`, options),
      lookupLocationEmail: (options) =>
        CallService(GET, `${BASE_URL}/leadsource/location-email`, options),
      saveLocationEmail: (options) =>
        CallService(POST, `${BASE_URL}/leadsource/location-email`, options),
    },
    business_tags: {
      get: (options) => CallService(GET, `${BASE_URL}/business_tags`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/business_tags`, options),
      create: (options) => CallService(POST, `${BASE_URL}/business_tags`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/business_tags`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/business_tags/filter`, options),
      csvupload: (options) => CallService(POST, `${BASE_URL}/business_tags/csvupload`, options),
      ReOrderTags: (options) => CallService(PUT, `${BASE_URL}/business_tags/ReOrderTags`, options),
    },
    topnav_categories: {
      get: (options) => CallService(GET, `${BASE_URL}/topnav_categories`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/topnav_categories`, options),
      create: (options) => CallService(POST, `${BASE_URL}/topnav_categories`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/topnav_categories`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/topnav_categories/filter`, options),
      ReOrderCategory: (options) => CallService(PUT, `${BASE_URL}/topnav_categories/ReOrderCategory`, options),

    },
    topnav_sub_categories: {
      get: (options) => CallService(GET, `${BASE_URL}/topnav_sub_categories`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/topnav_sub_categories`, options),
      create: (options) => CallService(POST, `${BASE_URL}/topnav_sub_categories`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/topnav_sub_categories`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/topnav_sub_categories/filter`, options),
      ReOrderSubCategory: (options) => CallService(PUT, `${BASE_URL}/topnav_sub_categories/ReOrderSubCategory`, options),

    },
    users: {
      get: (options) => CallService(GET, `${BASE_URL}/users`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/users`, options),
      create: (options) => CallService(POST, `${BASE_URL}/users`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/users`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/users/filter`, options),
    },
    stripe: {
      filter: (options) =>
        CallService(GET, `${BASE_URL}/agency/stripe/filter/${options.id}`, options),
      integrateAcocunt: (options) =>
        CallService(GET, `${BASE_URL}/agency/stripe/integrate`, options),
      get: (options) => CallService(GET, `${BASE_URL}/agency/stripe`, options),
      invoices: (options) =>
        CallService(GET, `${BASE_URL}/agency/stripe/invoices/${options.id}`, options),
      update: (options) =>
        CallService(POST, `${BASE_URL}/agency/stripe/update/${options.id}`, options),
      create: (options) => CallService(POST, `${BASE_URL}/agency/stripe/submit`, options),
      delete: (options) =>
        CallService(POST, `${BASE_URL}/agency/stripe/delete/${options.id}`, options),
    },
    notes: {
      get: (options) => CallService(GET, `${BASE_URL}/notes`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/notes`, options),
      create: (options) => CallService(POST, `${BASE_URL}/notes`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/notes/filter`, options),
    },
    reports: {
      customer: (options) => CallService(GET, `${BASE_URL}/reports/customer`, options),
      type_campaigns: (options) => CallService(GET, `${BASE_URL}/reports/type_campaigns`, options),
      transactions: (options) => CallService(GET, `${BASE_URL}/reports/transactions`, options),
      campaign: (options) => CallService(GET, `${BASE_URL}/reports/campaigns`, options),
      campaign_overview: (options) =>
        CallService(GET, `${BASE_URL}/reports/campaign_overview`, options),
      brand: (options) => CallService(GET, `${BASE_URL}/reports/brand`, options),
      agency: (options) => CallService(GET, `${BASE_URL}/reports/agency`, options),
      monthly_sale: (options) => CallService(GET, `${BASE_URL}/reports/monthly_sale`, options),
      agency_brands: (options) => CallService(GET, `${BASE_URL}/reports/agency_brands`, options),
    },
    Overview: {
      locations: (options) => CallService(GET, `${BASE_URL}/overview/locations`, options),
      ghlLocation: (options) => CallService(GET, `${BASE_URL}/overview/ghlLocation`, options),
      users: (options) => CallService(GET, `${BASE_URL}/overview/users`, options),
      update_location: (options) =>
        CallService(PUT, `${BASE_URL}/overview/update_location`, options),
      user_details: (options) => CallService(GET, `${BASE_URL}/overview/user_details`, options),
      updateUser: (options) => CallService(PUT, `${BASE_URL}/overview`, options),
      updatelocation: (options) => CallService(PUT, `${BASE_URL}/overview/location`, options),
    },
    customer: {
      get: (options) => CallService(GET, `${BASE_URL}/customer`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/customer`, options),
    },
    FetchEventData: {
      Get: (options) => CallService(GET, `${BASE_URL}/fetch_event_data`, options),
    },
    agency: {
      GetSubmissions: (options) => CallService(GET, `${BASE_URL}/agency/GetSubmissions`, options),

      get: (options) => CallService(GET, `${BASE_URL}/agency`, options),
      GetGhlMedia: (options) => CallService(GET, `${BASE_URL}/agency/GetGhlMedia`, options),
      enableWordpress: (options) => CallService(PUT, `${BASE_URL}/agency/enableWordpress`, options),
      GetByLocation: (options) => CallService(GET, `${BASE_URL}/agency/GetByLocation`, options),
      dashboard_data: (options) => CallService(GET, `${BASE_URL}/agency/dashboard_data`, options),
      GetStripeAccountName: (options) => CallService(GET, `${BASE_URL}/agency/GetStripeAccountName`, options),
      get_app_setup: (options) => CallService(GET, `${BASE_URL}/agency/get_app_setup`, options),
      getworkflows: (options) => CallService(GET, `${BASE_URL}/agency/getworkflows`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/agency`, options),
      UpdateStripe: (options) => CallService(PUT, `${BASE_URL}/agency/UpdateStripe`, options),
      ValidateApiKey: (options) => CallService(PUT, `${BASE_URL}/agency/ValidateApiKey`, options),
      update_stripe: (options) => CallService(PUT, `${BASE_URL}/agency/update_stripe`, options),
      AddBalance: (options) => CallService(PUT, `${BASE_URL}/agency/addbalance`, options),
      GetChargelogs: (options) => CallService(GET, `${BASE_URL}/agency/chargelogs`, options),
      update_agency_config: (options) =>
        CallService(PUT, `${BASE_URL}/agency/prompt-config`, options),
      getProducts: (options) => CallService(GET, `${BASE_URL}/agency/getproducts`, options),
      getWorkflows: (options) => CallService(GET, `${BASE_URL}/agency/getworkflows`, options),
      getbyplan_byId: (options) => CallService(GET, `${BASE_URL}/agency/getbyplan_byId`, options),
      search_stripe_customers: (options) =>
        CallService(GET, `${BASE_URL}/agency/find-customers`, options),
      ReInstallDomain: (options) =>
        CallService(POST, `${BASE_URL}/agency/ReInstallDomain`, options),
    },
    superadmin: {
      GET: (options) =>
        CallService(GET, `${BASE_URL}/super_admin_settings`, options),
    },
    profile: {
      get: (options) => CallService(GET, `${BASE_URL}/profile`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/profile`, options),
      create: (options) => CallService(POST, `${BASE_URL}/profile`, options),
    },
    chat: {
      get: (options) => CallService(GET, `${BASE_URL}/chat/`, options),
      getByAgency: (options) => CallService(GET, `${BASE_URL}/chat/getbyagency`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/chat/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/chat`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/chat/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/chat`, options),
      addquestion: (options) => CallService(POST, `${BASE_URL}/chat/addquestion`, options),
      getquestions: (options) => CallService(GET, `${BASE_URL}/chat/getquestions`, options),
      deleteQuestion: (options) => CallService(POST, `${BASE_URL}/chat/deleteQuestion`, options),
      editquestion: (options) => CallService(POST, `${BASE_URL}/chat/editquestion`, options),
      fileUploading: (options) =>
        CallService(POST, `${BASE_URL}/chat/assistants/files/upload`, options),
      fileDelete: (options) =>
        CallService(DELETE, `${BASE_URL}/chat/assistants/files/delete`, options),
    },
    messages: {
      get: (options) => CallService(GET, `${BASE_URL}/messages`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/messages/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/messages`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/messages/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/messages`, options),
    },
    notification: {
      get: (options) => CallService(GET, `${BASE_URL}/notifications`, options),
      read: (options) => CallService(PUT, `${BASE_URL}/notifications/read`, options),
    },
    webhook: {
      get: (options) => CallService(GET, `${BASE_URL}/webhook/`, options),
      checkout: (options) => CallService(GET, `${BASE_URL}/webhook/checkout`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/webhook/filter`, options),
      load_dummy_data: (options) =>
        CallService(GET, `${BASE_URL}/webhook/load_dummy_data`, options),
      auto_configure_data_mapping: (options) =>
        CallService(GET, `${BASE_URL}/webhook/auto_configure_data_mapping`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/webhook`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/webhook/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/webhook`, options),
      GetFunnels: (options) => CallService(GET, `${BASE_URL}/webhook/get_funnels`, options),
      GetForms: (options) => CallService(GET, `${BASE_URL}/webhook/get_forms`, options),
    },
    tickets: {
      get: (options) => CallService(GET, `${BASE_URL}/tickets`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/tickets/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/tickets`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/tickets/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/tickets`, options),
    },
    comments: {
      get: (options) => CallService(GET, `${BASE_URL}/comments`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/comments/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/comments`, options),
    },
    docs: {
      get: (options) => CallService(GET, `${BASE_URL}/docs`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/docs/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/docs`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/docs/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/docs`, options),
    },
    docs_category: {
      get: (options) => CallService(GET, `${BASE_URL}/docs/category`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/docs/category/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/docs/category`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/docs/category/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/docs/category`, options),
    },
    location: {
      get: (options) => CallService(GET, `${BASE_URL}/location`, options),
      user_locations: (options) => CallService(GET, `${BASE_URL}/location/user_locations`, options),
    },
    business: {
      get: (options) => CallService(GET, `${BASE_URL}/business`, options),
      by_agency: (options) => CallService(GET, `${BASE_URL}/business/by_agency`, options),
      by_location: (options) => CallService(GET, `${BASE_URL}/business/by_location`, options),
    },
    design: {
      get: (options) => CallService(GET, `${BASE_URL}/design`, options),
      create: (options) => CallService(POST, `${BASE_URL}/design`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/design`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/design`, options),
    },
    campaign: {
      get: (options) => CallService(GET, `${BASE_URL}/campaign`, options),
      create: (options) => CallService(POST, `${BASE_URL}/campaign`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/campaign`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/campaign`, options),
      passes: (options) => CallService(GET, `${BASE_URL}/campaign/passes`, options),
    },
    public: {
      get: (options) => CallService(GET, `${BASE_URL}/public`, options),
      enrollment: (options) => CallService(POST, `${BASE_URL}/public/enrollment`, options),
      enroll_now: (options) => CallService(POST, `${BASE_URL}/public/enrollnow`, options),
      get_enrollment: (options) => CallService(GET, `${BASE_URL}/public/enrollment`, options),
      pass: (options) => CallService(GET, `${BASE_URL}/public/pass`, options),
      passes: (options) => CallService(GET, `${BASE_URL}/public/passes`, options),
      subscriber: (options) => CallService(GET, `${BASE_URL}/public/subscriber`, options),
    },
    pass: {
      get: (options) => CallService(GET, `${BASE_URL}/pass`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/pass`, options),
    },
    growthtool: {
      get: (options) => CallService(GET, `${BASE_URL}/growthtool`, options),
      create: (options) => CallService(POST, `${BASE_URL}/growthtool`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/growthtool`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/growthtool`, options),
    },
    redemption: {
      redeem: (options) => CallService(POST, `${BASE_URL}/redemption`, options),
      giveaway: (options) => CallService(POST, `${BASE_URL}/redemption/giveaway`, options),
    },
    mailgun: {
      getdomains: (options) => CallService(GET, `${BASE_URL}/mailgun/emails`, options),
    },
    // ghl: {
    //   call   : (options) => CallService(POST, `${BASE_URL}/services/ghl`, options),
    //   oauth : (options) => CallService(GET, `${BASE_URL}/services/ghl/oauth/callback`, options),
    // },
    super_admin: {
      get: (options) => CallService(GET, `${BASE_URL}/super_admin/`, options),
      getproducts: (options) => CallService(GET, `${BASE_URL}/super_admin/getproducts`, options),
      add_team: (options) => CallService(GET, `${BASE_URL}/super_admin/add_team`, options),
      agencies: (options) => CallService(GET, `${BASE_URL}/super_admin/agencies`, options),
      customers: (options) => CallService(GET, `${BASE_URL}/super_admin/customers`, options),
    },
    saas: {
      createSaas: (options) => CallService(POST, `${BASE_URL}/saas`, options),
      editSaas: (options) => CallService(POST, `${BASE_URL}/saas/editSaas`, options),
      ReOrderPlan: (options) => CallService(PUT, `${BASE_URL}/saas/ReOrderPlan`, options),
      subscriptions: (options) => CallService(GET, `${BASE_URL}/saas/subscriptions`, options),
      getProductLocations: (options) =>
        CallService(GET, `${BASE_URL}/saas/getProductLocations`, options),
      potentialproducts: (options) =>
        CallService(GET, `${BASE_URL}/saas/potentialproducts`, options),
      create_accounts: (options) => CallService(POST, `${BASE_URL}/saas/create_accounts`, options),
      create_all_accounts: (options) =>
        CallService(POST, `${BASE_URL}/saas/create_all_accounts`, options),

      create_account_manually: (options) =>
        CallService(POST, `${BASE_URL}/saas/create_account_manually`, options),
      updateSaas: (options) => CallService(PUT, `${BASE_URL}/saas`, options),
      getSaas: (options) => CallService(GET, `${BASE_URL}/saas/filter`, options),
      deleteSaas: (options) => CallService(DELETE, `${BASE_URL}/saas/delete`, options),
      categorypricing: (options) => CallService(POST, `${BASE_URL}/saas/categorypricing`, options),
      addons: (options) => CallService(POST, `${BASE_URL}/saas/addons`, options),
      fetch_category_pricing: (options) =>
        CallService(POST, `${BASE_URL}/saas/fetch_category_pricing`, options),
      fetch_addons: (options) => CallService(POST, `${BASE_URL}/saas/fetch_addons`, options),
      agencysaas: (options) => CallService(GET, `${BASE_URL}/saas/agencysaas`, options),
      edit_category_pricing: (options) =>
        CallService(POST, `${BASE_URL}/saas/edit_category_pricing`, options),
      edit_addons: (options) => CallService(POST, `${BASE_URL}/saas/edit_addons`, options),
      subscription_setting: (options) =>
        CallService(POST, `${BASE_URL}/saas/subscription_setting`, options),
      delete_category_pricing: (options) =>
        CallService(POST, `${BASE_URL}/saas/delete_category_pricing`, options),
      delete_addons: (options) => CallService(POST, `${BASE_URL}/saas/delete_addons`, options),
      updateSaasConfigration: (options) =>
        CallService(POST, `${BASE_URL}/saas/updateSaasConfigration`, options),
      deleteSaasConfigration: (options) =>
        CallService(POST, `${BASE_URL}/saas/deleteSaasConfigration`, options),
      deletestripfromagency: (options) =>
        CallService(GET, `${BASE_URL}/saas/deletestripfromagency`, options),
      showonaccountlevel: (options) =>
        CallService(POST, `${BASE_URL}/saas/showonaccountlevel`, options),
      GetCoupens: (options) => CallService(GET, `${BASE_URL}/saas/GetCoupens`, options),
      CreateCoupen: (options) => CallService(POST, `${BASE_URL}/saas/CreateCoupen`, options),
      DeleteCoupen: (options) => CallService(DELETE, `${BASE_URL}/saas/DeleteCoupen`, options),
    },
    apps: {
      get: (options) => CallService(GET, `${BASE_URL}/apps/`, options),
    },
    upsales: {
      makecharge: (options) => CallService(POST, `${BASE_URL}/upsales/makecharge`, options),
      get: (options) => CallService(GET, `${BASE_URL}/upsales`, options),
    },
    product: {
      get: (options) => CallService(GET, `${BASE_URL}/product`, options),
      create: (options) => CallService(POST, `${BASE_URL}/product`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/product`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/product`, options),
    },
    utils: {
      upload_image: (options) => CallService(POST, `${BASE_URL}/utils/upload/image`, options),
    },
    services: {
      ghl: {
        call_service: (options) => CallService(POST, `${BASE_URL}/services/ghl/`, options),
      },
    },
    ghl: {
      call: (options) => CallService(POST, `${BASE_URL}/services/ghl`, options),
      refresh_token: (options) =>
        CallService(POST, `${BASE_URL}/services/ghl/refresh_token`, options),
    },
    promptslabel: {
      create: (options) => CallService(POST, `${BASE_URL}/promptslabel/create`, options),
      Get: (options) => CallService(GET, `${BASE_URL}/promptslabel`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/promptslabel`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/promptslabel`, options),
      GetselectvaluesbyID: (options) =>
        CallService(GET, `${BASE_URL}/promptslabel/GetselectvaluesbyID`, options),
    },
    assistant: {
      Get: (options) => CallService(GET, `${BASE_URL}/modalcreate`, options),
      create: (options) => CallService(POST, `${BASE_URL}/assistant`, options),
      Getassistant: (options) => CallService(GET, `${BASE_URL}/assistant`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/assistant`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/assistant`, options),
      Custom: (options) => CallService(POST, `${BASE_URL}/assistant/custom`, options),
      Getplugin: (options) => CallService(GET, `${BASE_URL}/assistant/functions`, options),
    },
    template: {
      Get: (options) => CallService(GET, `${BASE_URL}/modalcreate`, options),
      create: (options) => CallService(POST, `${BASE_URL}/assistant_template`, options),
      Gettemplate: (options) => CallService(GET, `${BASE_URL}/assistant_template`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/assistant_template`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/assistant_template`, options),
      Custom: (options) => CallService(POST, `${BASE_URL}/assistant_template/custom`, options),
    },
    jobs: {
      Get: (options) => CallService(GET, `${BASE_URL}/jobs`, options),
      Create: (options) => CallService(POST, `${BASE_URL}/jobs`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/jobs`, options),
      Update: (options) => CallService(PUT, `${BASE_URL}/jobs`, options),
    },
    events: {
      Get: (options) => CallService(GET, `${BASE_URL}/events`, options),
      Create: (options) => CallService(POST, `${BASE_URL}/events`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/events`, options),
      Update: (options) => CallService(PUT, `${BASE_URL}/events`, options),
    },
    blogs: {
      Get: (options) => CallService(GET, `${BASE_URL}/blogs`, options),
      Create: (options) => CallService(POST, `${BASE_URL}/blogs`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/blogs`, options),
      Update: (options) => CallService(PUT, `${BASE_URL}/blogs`, options),
    },
    changelogs: {
      Get: (options) => CallService(GET, `${BASE_URL}/changelogs`, options),
    },
    newsFeed: {
      Get: (options) => CallService(GET, `${BASE_URL}/news_Feed`, options),
      Create: (options) => CallService(POST, `${BASE_URL}/news_Feed`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/news_Feed`, options),
      Update: (options) => CallService(PUT, `${BASE_URL}/news_Feed`, options),
    },
    themes: {
      get: (options) => CallService(GET, `${BASE_URL}/themes`, options),
      DomainConnection: (options) =>
        CallService(POST, `${BASE_URL}/themes/DomainConnection`, options),
      selectTheme: (options) => CallService(POST, `${BASE_URL}/themes/selectTheme`, options),
      create: (options) => CallService(POST, `${BASE_URL}/themes`, options),
      getListOfFunnels: (options) =>
        CallService(GET, `${BASE_URL}/themes/getListOfFunnels`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/themes`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/themes`, options),
      updateOrCreateByAgencyId: (options) =>
        CallService(POST, `${BASE_URL}/themes/updateOrCreateByAgencyId`, options),
      ReInstallDomain: (options) =>
        CallService(POST, `${BASE_URL}/themes/ReInstallDomain`, options),
      ActiveOrDeActive: (options) =>
        CallService(POST, `${BASE_URL}/themes/ActiveOrDeActive`, options),
    },
    reviews: {
      get: (options) => CallService(GET, `${BASE_URL}/reviews`, options),
      GetFromGoogle: (options) => CallService(GET, `${BASE_URL}/reviews/google`, options),
      GetGoogleImages: (options) =>
        CallService(GET, `${BASE_URL}/reviews/GetGoogleImages`, options),
      create: (options) => CallService(POST, `${BASE_URL}/reviews`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/reviews`, options),
      updateStatus: (options) => CallService(PUT, `${BASE_URL}/reviews/status`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/reviews`, options),
    },
    AccessRequest: {
      create: (options) => CallService(POST, `${BASE_URL}/AccessRequest`, options),
      get: (options) => CallService(GET, `${BASE_URL}/AccessRequest`, options),
      GetLocationRequest: (options) => CallService(GET, `${BASE_URL}/AccessRequest/GetLocationRequest`, options),
    },
    claim_business: {
      create: (options) => CallService(POST, `${BASE_URL}/claim_business`, options),
      AssignConsumer: (options) => CallService(POST, `${BASE_URL}/claim_business/AssignConsumer`, options),
      unClaimListing: (options) => CallService(POST, `${BASE_URL}/claim_business/unClaimListing`, options),
      Get: (options) => CallService(GET, `${BASE_URL}/claim_business`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/claim_business`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/claim_business`, options),
    },
    followBusiness: {
      create: (options) => CallService(POST, `${BASE_URL}/follow`, options),
      Get: (options) => CallService(GET, `${BASE_URL}/follow`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/follow`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/follow`, options),
    },
    JobApplication: {
      create: (options) => CallService(POST, `${BASE_URL}/job_Application`, options),
      Get: (options) => CallService(GET, `${BASE_URL}/job_Application`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/job_Application`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/job_Application`, options),
    },
  };

  return APIs;
}

const useUploadImage = () => {
  const AppService = useAppServices();
  return ({ toaster, file, desiredPath }) => {
    const form = new FormData();
    form.append("image", file, file.name);
    return AppService.utils.upload_image({
      toaster,
      payload: form,
      query: `desiredPath=${desiredPath}`,
    });
  };
};

// async function GetuploadImage(image_name, access_token) {
//   let config = {
//     method: "get",
//     maxBodyLength: Infinity,
//     url: "https://services.leadconnectorhq.com/medias/files?type=file&query=" + image_name,
//     headers: {
//       Authorization: "Bearer " + access_token,
//       Version: "2021-07-28",
//     },
//   };
//   console.log(access_token, "access_token");
//   const response_data = await axios
//     .request(config)
//     .then((response) => {
//       console.log(JSON.stringify(response.data));
//       return {
//         success: true,
//         data: response.data.files[0].url,
//       };
//     })
//     .catch((error) => {
//       console.log(error);
//       return {
//         success: false,
//         data: error,
//       };
//     });
//   return response_data;
// }
async function uploadImageToGhl(image, agency, authToken) {
  const apiUrl = `${BASE_URL}/accounts/upload-img-to-ghl`;
  const formData = new FormData();

  if (!agency?._id) {
    return { success: false, data: "Agency not found. Please refresh and try again." };
  }

  formData.append("image", image);
  formData.append("agency_id", agency._id);

  const headers = {
    "Content-Type": "multipart/form-data",
  };
  if (authToken) {
    headers["x-auth-token"] = `Bearer ${authToken}`;
  }

  try {
    const response = await axios.post(apiUrl, formData, { headers });

    if (response.data && response.data.success === false) {
      return {
        success: false,
        data: response.data.message || "Upload failed",
      };
    }

    const url =
      response.data?.data?.url ||
      response.data?.data?.media?.url ||
      response.data?.url;
    if (!url) {
      return {
        success: false,
        data: "Upload succeeded but no image URL returned",
      };
    }
    return {
      success: true,
      data: url,
    };
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      "Image upload failed. If you see 'GHL access token missing', connect GoHighLevel in Integrations.";
    console.error("Upload error:", message);
    return {
      success: false,
      data: message,
    };
  }
}


export { useAppServices, useCallService, useUploadImage, uploadImageToGhl };
